import { useMemo } from "react";
import { useConvertColors } from "../../../../../Shared/hooks/useConvertColors/useConvertColors";
import { useOrderFlow } from "../../../../OrderFlowProvider/OrderFlowContext";
import { ModuleNames, RecipeSelectionType as RST, } from "../../../../OrderFlowProvider/OrderFlowTypes";
import { RecipeModuleText } from "../utils/RecipeModule-Types";
/**
 * Custom hook that retrieves and processes recipe module configuration
 * from the order flow store based on sequence number and current page.
 *
 * @param sequenceNumber - The sequence number to identify the specific recipe module
 * @returns Processed configuration object with feature flags and settings
 * @throws Error if sequenceNumber is not provided or module config not found
 */
export const useRecipeModuleConfig = (sequenceNumber) => {
    var _a, _b, _c, _d, _e, _f;
    if (!sequenceNumber) {
        throw new Error("Sequence number is required for useRecipeModuleConfig");
    }
    const { store } = useOrderFlow();
    const unmodifiedModuleConfig = (_a = store.model.Flow.FlowModules.find((module) => module.ModuleName === ModuleNames.RECIPE &&
        module.Sequence === sequenceNumber &&
        module.Page === store.cart.currentPage)) === null || _a === void 0 ? void 0 : _a.ModuleConfig;
    // Defensive: in case moduleConfig is undefined
    if (!unmodifiedModuleConfig) {
        throw new Error(`ModuleConfig not found for sequence ${sequenceNumber}`);
    }
    const { ExtraRecipePriceColor, ReducedRecipePriceColor } = unmodifiedModuleConfig;
    const updatedColors = useConvertColors({
        ExtraRecipePriceColor,
        ReducedRecipePriceColor,
    });
    const moduleConfig = Object.assign(Object.assign({}, unmodifiedModuleConfig), updatedColors);
    const noResultsAvailableText = (_b = moduleConfig.NoResultsAvailableText) !== null && _b !== void 0 ? _b : RecipeModuleText.NoRecipesAvailableText;
    const recipeSelectionType = moduleConfig === null || moduleConfig === void 0 ? void 0 : moduleConfig.RecipeSelectionType;
    const enableAddMeals = recipeSelectionType == RST.AddMeals || recipeSelectionType == RST.Both;
    const enableAssignRecipes = recipeSelectionType == RST.AssignRecipes || recipeSelectionType == RST.Both;
    const enableRemoveMeals = recipeSelectionType == RST.Both && moduleConfig.MealRemoval !== undefined;
    const recipeSortOption = store.cart.recipeSortOption;
    const recipeImageBaseUrl = store.model.RecipeImageBaseUrl;
    const recipeAutoSelection = moduleConfig.RecipeAutoSelection;
    const enableCostSorting = moduleConfig.EnableCostSorting;
    const enableCustomerSorting = moduleConfig.EnableCustomerSorting;
    const paperSettings = moduleConfig.PaperSetting;
    // The 'singleRecipeSelectLimit' variable is specifically used for toggle functions
    // where the single recipe selection limit is expected to be a number.If the value is an empty string
    // or cannot be coerced into a number, it defaults to 1. This ensures that the toggle functions behave
    // as expected, even if the config value is missing, invalid, or empty.
    //
    // The default value of 1 ensures that users can still add a recipe, preventing the app from
    // failing silently or behaving unexpectedly with invalid values.
    const singleRecipeSelectLimit = ((_d = (_c = moduleConfig === null || moduleConfig === void 0 ? void 0 : moduleConfig.SingleRecipeSelectionModeLimit) === null || _c === void 0 ? void 0 : _c.NumericValue) === null || _d === void 0 ? void 0 : _d.trim()) === "" ||
        isNaN(Number((_e = moduleConfig === null || moduleConfig === void 0 ? void 0 : moduleConfig.SingleRecipeSelectionModeLimit) === null || _e === void 0 ? void 0 : _e.NumericValue))
        ? 1
        : Number((_f = moduleConfig === null || moduleConfig === void 0 ? void 0 : moduleConfig.SingleRecipeSelectionModeLimit) === null || _f === void 0 ? void 0 : _f.NumericValue);
    return useMemo(() => ({
        enableAddMeals,
        enableAssignRecipes,
        enableCostSorting,
        enableCustomerSorting,
        enableRemoveMeals,
        moduleConfig,
        noResultsAvailableText,
        paperSettings,
        recipeAutoSelection,
        recipeImageBaseUrl,
        recipeSortOption,
        singleRecipeSelectLimit,
    }), [
        enableAddMeals,
        enableAssignRecipes,
        enableCostSorting,
        enableCustomerSorting,
        enableRemoveMeals,
        moduleConfig,
        noResultsAvailableText,
        paperSettings,
        recipeAutoSelection,
        recipeImageBaseUrl,
        recipeSortOption,
        singleRecipeSelectLimit,
    ]);
};
